import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from '@mui/material';
import { useEffect, useState } from 'react';
import AssessmentScheduleFields, { type AssessmentScheduleValues } from './AssessmentScheduleFields';
import { getBrowserTimeZone, isValidTimeZone, toZonedInputValue } from '../utils/assessmentSchedule';

export type AssessmentDatesPayload = {
  opens_at?: string;
  closes_at?: string;
  schedule_timezone: string;
};

interface EditAssessmentDatesDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: AssessmentDatesPayload) => void;
  isSubmitting: boolean;
  initialOpensAt?: string | null;
  initialClosesAt?: string | null;
  initialTimeZone?: string | null;
}

function EditAssessmentDatesDialog({
  open,
  onClose,
  onSubmit,
  isSubmitting,
  initialOpensAt,
  initialClosesAt,
  initialTimeZone,
}: EditAssessmentDatesDialogProps) {
  const [schedule, setSchedule] = useState<AssessmentScheduleValues>({ opensAt: '', closesAt: '', timeZone: '' });
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;

    const timeZone = initialTimeZone && isValidTimeZone(initialTimeZone) ? initialTimeZone : getBrowserTimeZone();
    setSchedule({
      opensAt: toZonedInputValue(initialOpensAt, timeZone),
      closesAt: toZonedInputValue(initialClosesAt, timeZone),
      timeZone,
    });
    setLocalError(null);
    // Only reseed the form when the dialog opens, not when a background refetch updates the dates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSubmit = () => {
    if (schedule.opensAt && schedule.closesAt && schedule.closesAt <= schedule.opensAt) {
      setLocalError('Close time must be after open time.');
      return;
    }

    setLocalError(null);
    onSubmit({
      opens_at: schedule.opensAt || undefined,
      closes_at: schedule.closesAt || undefined,
      schedule_timezone: schedule.timeZone,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={() => !isSubmitting && onClose()}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Edit Active Dates</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {localError && <Alert severity="error">{localError}</Alert>}
          <AssessmentScheduleFields value={schedule} onChange={setSchedule} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : 'Save Dates'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default EditAssessmentDatesDialog;
